import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { API_URL } from "../config/API_URLS.tsx";
import { authenticatedFetch } from "../auth/RequestSigner.tsx";

export function UserDetails() {
    const [loading, setLoading] = useState(false);
    const [userData, setUserData] = useState(null);
    const [error, setError] = useState(null);
    const [statusLoading, setStatusLoading] = useState(false);
    const { userId } = useParams();
    const roll = parseInt(localStorage.getItem('userRoll'));

    // Obtener el tipo de usuario de los query params
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const userType = queryParams.get('userType') || 'aprendices';

    const fetchUserDetails = useCallback(async () => {
        try {
            setLoading(true);

            // Seleccionar el endpoint según el tipo de usuario
            let endpoint = '';
            switch(userType) {
                case 'aprendices':
                    endpoint = `${API_URL}/v1/aprendices/id/document/${userId}`;
                    break;
                case 'vigilantes':
                    endpoint = `${API_URL}/v1/vigilante-by-id/${userId}`;
                    break;
                case 'admins':
                    endpoint = `${API_URL}/v1/admin-by-id/${userId}`;
                    break;
                default:
                    endpoint = `${API_URL}/v1/aprendices/id/document/${userId}`;
            }

            const response = await authenticatedFetch(endpoint, {
                method: 'get',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`Error al cargar los detalles del ${userType === 'aprendices' ? 'aprendiz' : userType === 'vigilantes' ? 'vigilante' : 'administrador'}`);
            }

            const data = await response.json();
            setUserData(data);
            setLoading(false);
        } catch (error) {
            console.error('Error:', error);
            setError(error.message);
            setLoading(false);
        }
    }, [userId, userType]);

    useEffect(() => {
        fetchUserDetails();
    }, [fetchUserDetails]);

    // Función para cambiar el estado del usuario
    const handleStatusChange = async () => {
        try {
            setStatusLoading(true);

            // Determinar el nuevo estado (si está activo, inactivarlo y viceversa)
            const newStatusId = userData.status_id === 2 ? 3 : 2;

            // Seleccionar el endpoint según el tipo de usuario
            let endpoint = '';
            switch(userType) {
                case 'aprendices':
                    endpoint = `${API_URL}/v1/aprendiz-change-status`;
                    break;
                case 'vigilantes':
                    endpoint = `${API_URL}/v1/vigilante-change-status`;
                    break;
                case 'admins':
                    endpoint = `${API_URL}/v1/admin-change-status`;
                    break;
                default:
                    endpoint = `${API_URL}/v1/aprendiz-change-status`;
            }

            const response = await authenticatedFetch(endpoint, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    id: userData.id,
                    status_id: newStatusId
                })
            });

            if (!response.ok) {
                throw new Error(`Error al cambiar el estado del ${userType === 'aprendices' ? 'aprendiz' : userType === 'vigilantes' ? 'vigilante' : 'administrador'}`);
            }

            // Actualizar los datos del usuario después de cambiar el estado
            fetchUserDetails();
            setStatusLoading(false);
        } catch (error) {
            console.error('Error:', error);
            setError(error.message);
            setStatusLoading(false);
        }
    };

    // Función para obtener el color del estado
    const getStatusColor = (status) => {
        switch (status) {
            case 1:
                return 'bg-yellow-100 text-yellow-800';
            case 2:
                return 'bg-green-300 text-green-900';
            case 3:
                return 'bg-red-100 text-red-800';
            case 4:
                return 'bg-yellow-300 text-orange-800';
            case 5:
                return 'bg-green-100 text-green-600';
            case 6:
                return 'bg-red-200 text-red-900';
            default:
                return 'bg-yellow-100 text-yellow-800';
        }
    };

    // Función para obtener el texto del estado
    const getStatusText = (status) => {
        switch (status) {
            case 1:
                return 'bloqueado';
            case 2:
                return 'Activo';
            case 3:
                return 'Inactivo';
            case 4:
                return 'Recibido';
            case 5:
                return 'Aceptado';
            case 6:
                return 'Rechazado';
            default:
                return 'Pendiente';
        }
    };

    // Función para obtener el texto del rol
    const getRoleText = (role) => {
        switch (role) {
            case 1:
                return 'Administrador';
            case 2:
                return 'Aprendiz';
            case 3:
                return 'Vigilante';
            default:
                return 'Usuario';
        }
    };

    return (
        <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-8" data-testid="user-details-container">
            {loading && (
                <div className="flex items-center justify-center p-8 min-h-[300px]" data-testid="user-details-loading">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-700"></div>
                    <span className="ml-3 text-gray-700">Cargando información del usuario...</span>
                </div>
            )}

            {!loading && userData && (
                <div className="flex flex-col gap-6" data-testid="user-details-wrapper">
                    <div className="flex justify-between items-center mb-2">
                        <h1 className="text-2xl font-bold text-gray-800" data-testid="user-details-page-title">
                            Detalle de Usuario
                        </h1>
                        <Link 
                            to="/users-administration-list" 
                            className="px-4 py-2 text-white text-sm font-semibold rounded-lg shadow-md flex items-center justify-center relative overflow-hidden group"
                            data-testid="user-details-back-button"
                        >
                            <div className="absolute inset-0 bg-gradient-to-t from-amber-300 to-amber-700 transition-transform duration-700 ease-in-out"></div>
                            <div className="absolute inset-0 bg-gradient-to-b from-amber-300 to-amber-700 opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-in-out"></div>
                            <span className="z-10 text-gray-900">Volver a la lista</span>
                        </Link>
                    </div>

                    <div className="bg-white rounded-lg shadow-md overflow-hidden" data-testid="user-details-content">
                        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-amber-100">
                            <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                                <div data-testid="user-header-info">
                                    <h2 className="text-xl font-bold text-gray-800" data-testid="user-name">
                                        {userData.name} {userData.last_name}
                                    </h2>
                                    <p className="text-gray-600 text-sm mt-1" data-testid="user-email">
                                        {userData.email}
                                    </p>
                                </div>
                                <span 
                                    className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${getStatusColor(userData.status_id)}`}
                                    data-testid="user-status-badge"
                                >
                                    {getStatusText(userData.status_id)}
                                </span>
                            </div>
                        </div>

                        <div className="p-6">
                            <div className="grid md:grid-cols-2 gap-8">
                                <div data-testid="user-personal-info">
                                    <div className="mb-6">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-2 border-b border-gray-200 pb-2" data-testid="user-personal-info-title">
                                            Información Personal
                                        </h3>
                                        <div className="grid grid-cols-2 gap-y-3 gap-x-6 text-sm" data-testid="user-personal-info-list">
                                            <div>
                                                <span className="text-gray-500">ID:</span>
                                                <p className="text-gray-800 font-medium" data-testid="user-id-value">
                                                    {userData.id}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-gray-500">Nombre:</span>
                                                <p className="text-gray-800 font-medium" data-testid="user-name-value">
                                                    {userData.name} {userData.last_name}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-gray-500">Documento:</span>
                                                <p className="text-gray-800 font-medium" data-testid="user-document-value">
                                                    {userData.document || 'No disponible'}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-gray-500">Email:</span>
                                                <p className="text-gray-800 font-medium" data-testid="user-email-value">
                                                    {userData.email}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-gray-500">Teléfono:</span>
                                                <p className="text-gray-800 font-medium" data-testid="user-phone-value">
                                                    {userData.phone || 'No disponible'}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-gray-500">Estado:</span>
                                                <p className={`font-medium ${getStatusColor(userData.status_id)}  text-center rounded-full `}  data-testid="user-status-value">
                                                    {getStatusText(userData.status_id)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mb-6">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-2 border-b border-gray-200 pb-2" data-testid="user-role-info-title">
                                            Información de Rol
                                        </h3>
                                        <div className="grid grid-cols-2 gap-y-3 gap-x-6 text-sm" data-testid="user-role-info-list">
                                            <div>
                                                <span className="text-gray-500">Rol:</span>
                                                <p className="text-gray-800 font-medium" data-testid="user-role-value">
                                                    {getRoleText(userData.role_id)}
                                                </p>
                                            </div>
                                            {/* <div>
                                                <span className="text-gray-500">Área:</span>
                                                <p className="text-gray-800 font-medium" data-testid="user-area-value">
                                                    {userData.area || 'No especificada'}
                                                </p>
                                            </div> */}
                                            {userData.roll_id === 3 && (
                                                <div>
                                                    <span className="text-gray-500">Programa:</span>
                                                    <p className="text-gray-800 font-medium" data-testid="user-program-value">
                                                        {userData.program || 'No especificado'}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-6" data-testid="user-additional-info">
                                    {userData.photo && (
                                        <div className="mb-4" data-testid="user-image-container">
                                            <h3 className="text-lg font-semibold text-gray-800 mb-2 border-b border-gray-200 pb-2" data-testid="user-photo-title">
                                                Foto de Perfil
                                            </h3>
                                            <div className="bg-gray-100 rounded-lg overflow-hidden">
                                                <img 
                                                    className="w-full h-auto object-contain max-h-64 mx-auto" 
                                                    alt="Foto de perfil" 
                                                    src={userData.photo} 
                                                    data-testid="user-photo"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {roll === 1 && (
                                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-testid="user-actions-container">
                                            <h3 className="text-lg font-semibold text-gray-800 mb-3" data-testid="user-actions-title">
                                                Acciones
                                            </h3>

                                            <div className="flex flex-col sm:flex-row gap-3">
                                                <Link
                                                    to={`/edit-user?document=${userData.id}&role=${userData.role_id}`}
                                                    className="w-full px-4 py-2 text-gray-900 text-sm font-semibold rounded-lg shadow-md flex items-center justify-center relative overflow-hidden group"
                                                    data-testid="user-edit-button"
                                                >
                                                    <div className="absolute inset-0 bg-gradient-to-t from-amber-300 to-amber-700 transition-transform duration-700 ease-in-out"></div>
                                                    <div className="absolute inset-0 bg-gradient-to-b from-amber-300 to-amber-700 opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-in-out"></div>
                                                    <span className="z-10">Editar Usuario</span>
                                                </Link>

                                                <button  
                                                    type="button"
                                                    onClick={handleStatusChange}
                                                    disabled={statusLoading}
                                                    className={`w-full px-4 py-2 text-white text-sm font-semibold rounded-lg shadow-md flex items-center justify-center relative overflow-hidden group ${userData.status_id === 2 ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'} ${statusLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                                                    data-testid="user-toggle-status-button"
                                                >
                                                    <span className="z-10">
                                                        {statusLoading ? (
                                                            <>
                                                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                </svg>
                                                                Procesando...
                                                            </>
                                                        ) : (
                                                            <>{userData.status_id === 2 ? "Desactivar" : "Activar"} Usuario</>
                                                        )}
                                                    </span>
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {!loading && error && (
                <div className="bg-white rounded-lg shadow-md p-6 text-center" data-testid="user-details-error">
                    <div className="text-amber-600 text-5xl mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h2 className="text-xl font-bold text-gray-800 mb-2">No se pudo cargar la información del usuario</h2>
                    <p className="text-gray-600 mb-6">Lo sentimos, pero no hemos podido encontrar la información solicitada.</p>
                    <Link 
                        to="/users-administration-list" 
                        className="px-4 py-2 text-gray-900 text-sm font-semibold rounded-lg shadow-md inline-flex items-center justify-center relative overflow-hidden group"
                    >
                        <div className="absolute inset-0 bg-gradient-to-t from-amber-300 to-amber-700 transition-transform duration-700 ease-in-out"></div>
                        <div className="absolute inset-0 bg-gradient-to-b from-amber-300 to-amber-700 opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-in-out"></div>
                        <span className="z-10 text-gray-900">Volver a la lista de usuarios</span>
                    </Link>
                </div>
            )}
        </div>
    );
}
